import { useCallback, useEffect, useState } from 'react';
import {
  fetchMyVisits,
  type ProfileVisit,
  type ProfileVisitPeriod,
} from '../socialApi';

const PERIODS: Array<{ value: ProfileVisitPeriod; label: string }> = [
  { value: 'today', label: 'Сегодня' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'all', label: 'Все' },
];

function formatMoney(amount: string): string {
  const value = Number(amount);
  return Number.isFinite(value)
    ? `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`
    : amount;
}

function formatDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date);
}

function VisitSkeleton() {
  return (
    <div className="profile-visits-list" aria-hidden="true">
      {Array.from({ length: 4 }, (_, index) => (
        <div className="profile-visit-row profile-visit-row--skeleton" key={index}>
          <span className="social-skeleton profile-visit-row__marker" />
          <span className="profile-visit-row__copy">
            <span className="social-skeleton social-skeleton--line is-medium" />
            <span className="social-skeleton social-skeleton--line is-short" />
          </span>
        </div>
      ))}
    </div>
  );
}

type VisitHistorySectionProps = {
  impressionVisitIds: Set<string>;
  onCreateImpression?: (visitId: string, venueName: string) => void;
  onOpenVenue: (venueId: string) => void;
};

export function VisitHistorySection({
  impressionVisitIds,
  onCreateImpression,
  onOpenVenue,
}: VisitHistorySectionProps) {
  const [period, setPeriod] = useState<ProfileVisitPeriod>('all');
  const [items, setItems] = useState<ProfileVisit[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextPeriod: ProfileVisitPeriod) => {
    setLoading(true);
    setError(null);
    setCursor(null);
    setHasMore(false);

    try {
      const response = await fetchMyVisits(nextPeriod, 20);
      setItems(response.visits.items);
      setCursor(response.visits.pagination.next_cursor);
      setHasMore(response.visits.pagination.has_more);
    } catch {
      setItems([]);
      setError('Не удалось загрузить историю посещений');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(period);
  }, [load, period]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    setError(null);

    try {
      const response = await fetchMyVisits(period, 20, cursor);
      setItems((current) => [...current, ...response.visits.items]);
      setCursor(response.visits.pagination.next_cursor);
      setHasMore(response.visits.pagination.has_more);
    } catch {
      setError('Не удалось загрузить продолжение истории');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, period]);

  return (
    <section className="profile-reference-card profile-visits-card" id="profile-recent-visits">
      <div className="profile-reference-card__heading profile-reference-card__heading--visits">
        <h2>История посещений</h2>
        <div className="profile-period-tabs" role="tablist" aria-label="Период истории посещений">
          {PERIODS.map((item) => (
            <button
              className={period === item.value ? 'is-active' : ''}
              key={item.value}
              type="button"
              role="tab"
              aria-selected={period === item.value}
              onClick={() => setPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <VisitSkeleton />
      ) : error && items.length === 0 ? (
        <p className="profile-inline-state">{error}</p>
      ) : items.length === 0 ? (
        <p className="profile-inline-state">За этот период подтверждённых визитов нет.</p>
      ) : (
        <>
          <div className="profile-visits-list">
            {items.map((visit) => {
              const hasImpression = impressionVisitIds.has(visit.id);
              const venueName = visit.venue.name ?? 'Место Hopper';

              return (
                <article className="profile-visit-row" key={visit.id}>
                  <span className="profile-visit-row__marker" aria-hidden="true">✓</span>
                  <div className="profile-visit-row__copy">
                    <button type="button" className="profile-visit-row__venue" onClick={() => onOpenVenue(visit.venue.id)}>
                      {venueName}
                    </button>
                    <div className="profile-visit-row__meta">
                      <span>{formatDate(visit.local_date)}</span>
                      <span>{visit.xp.toLocaleString('ru-RU')} XP</span>
                      <span>{formatMoney(visit.total_amount)}</span>
                    </div>
                  </div>
                  <div className="profile-visit-row__action">
                    {hasImpression ? (
                      <span className="profile-impression-status"><b aria-hidden="true">✓</b> Впечатление</span>
                    ) : onCreateImpression ? (
                      <button type="button" onClick={() => onCreateImpression(visit.id, venueName)}>Оставить впечатление</button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>

          {error && <p className="profile-inline-state">{error}</p>}
          {hasMore && (
            <button
              className="profile-secondary-button profile-load-more"
              type="button"
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore ? 'Загружаем…' : 'Показать ещё'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
